/**
 * KAN-846: вход в приложение менеджера по логину и паролю (/m/login).
 * Логин - почта или телефон. После входа /m: оттуда экран "Начало работы"
 * откроется сам, если обязательные шаги не сделаны (OnboardingAutoOpen).
 * Логика входа в usePasswordLogin, здесь только форма.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { MobileScreen } from '@/components/mobile/MobileScreen';
import { ProfileField } from '@/components/mobile/ProfileField';
import { usePasswordLogin } from '@/hooks/usePasswordLogin';
import { MOBILE_HOME } from '@/lib/mobileApp';

export const PASSWORD_LOGIN_TEXTS = {
  title: 'Вход',
  login: 'Почта или телефон',
  password: 'Пароль',
  submit: 'Войти',
  required: 'Введите логин и пароль',
  noPassword: 'Пароля ещё нет? Попросите ссылку для входа у руководителя.',
} as const;

const ID_PREFIX = 'm-login';

export function MobilePasswordLoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const { submit, pending, error } = usePasswordLogin(() => navigate(MOBILE_HOME, { replace: true }));

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!login.trim() || !password) {
      setLocalError(PASSWORD_LOGIN_TEXTS.required);
      return;
    }
    setLocalError(null);
    submit(login, password);
  };

  const shownError = localError ?? error;

  return (
    <MobileScreen>
      <form onSubmit={onSubmit} noValidate className="space-y-4 text-left" data-testid="m-password-login">
        <h1 className="text-center text-lg font-semibold text-gray-900">{PASSWORD_LOGIN_TEXTS.title}</h1>
        <ProfileField
          idPrefix={ID_PREFIX}
          name="login"
          label={PASSWORD_LOGIN_TEXTS.login}
          value={login}
          onChange={setLogin}
          autoComplete="username"
          autoCapitalize="none"
          inputMode="email"
          maxLength={254}
        />
        <ProfileField
          idPrefix={ID_PREFIX}
          name="password"
          label={PASSWORD_LOGIN_TEXTS.password}
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          maxLength={128}
        />
        {shownError && (
          <p role="alert" className="text-sm text-red-600" data-testid="m-password-login-error">
            {shownError}
          </p>
        )}
        <button
          type="submit"
          disabled={pending}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-base font-medium text-white active:bg-blue-700 disabled:opacity-60"
        >
          {pending && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
          {PASSWORD_LOGIN_TEXTS.submit}
        </button>
        <p className="text-center text-sm text-gray-600">{PASSWORD_LOGIN_TEXTS.noPassword}</p>
      </form>
    </MobileScreen>
  );
}
